import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  opsApi, apiError, fmtINR2, fmtDate, GRAD, FONT_HEAD, StatusPill, APPROVAL_STATUS,
  Pagination, ROLE_LABEL,
} from './opsApi';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { BadgeCheck, Check, X, ArrowUpCircle } from 'lucide-react';
import Loader from '../../components/Loader';

export default function OpsApprovals({ user }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('pending');
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    opsApi.get('/approvals', { params: { page, page_size: 25, status: filter || undefined } })
      .then((r) => setData(r.data));
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  const decide = async () => {
    setBusy(true);
    try {
      await opsApi.post(`/approvals/${decision.approval.id}/decide`, { action: decision.action, note });
      toast.success(`Request ${decision.action === 'approve' ? 'approved' : 'rejected'}`);
      setDecision(null);
      setNote('');
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="ops-approvals-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={FONT_HEAD}>Approvals</h1>
        <p className="text-sm text-slate-500 mt-1">Discounts &amp; refunds escalate UP the hierarchy: rep → state manager → zone admin → super admin</p>
      </div>

      <div className="flex gap-1.5">
        {['pending', 'approved', 'rejected', ''].map((s) => (
          <button key={s || 'all'} data-testid={`approval-filter-${s || 'all'}`} onClick={() => { setFilter(s); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize transition-colors ${
              filter === s ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
            }`}
            style={filter === s ? { background: GRAD } : undefined}>
            {s || 'All'}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {!data ? (
          <Loader />
        ) : data.items.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <BadgeCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No {filter || ''} approval requests.</p>
          </div>
        ) : (
          <>
            {data.items.map((a) => (
              <div key={a.id} data-testid={`approval-row-${a.id}`} className="px-6 py-4 border-b border-slate-50 flex items-center gap-4 flex-wrap">
                <div className={`w-14 h-14 rounded-xl border flex flex-col items-center justify-center shrink-0 ${a.kind === 'discount' ? 'bg-amber-50 border-amber-100' : 'bg-purple-50 border-purple-100'}`}>
                  {a.kind === 'discount' ? (
                    <>
                      <span className="text-lg font-bold text-amber-600 tabular-nums leading-none">{a.discount_pct}%</span>
                      <span className="text-[9px] text-amber-500 uppercase">discount</span>
                    </>
                  ) : (
                    <>
                      <span className="text-xs font-bold text-purple-600 tabular-nums leading-none">{fmtINR2(a.amount).replace('.00', '')}</span>
                      <span className="text-[9px] text-purple-500 uppercase mt-0.5">refund</span>
                    </>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/sales/institutions/${a.institution_id}`} className="text-sm font-medium text-slate-800 hover:text-[#3B82F6]">{a.institution_name}</Link>
                  <div className="text-xs text-slate-500 mt-0.5">{a.reason}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span>By {a.requested_by_name} ({ROLE_LABEL[a.requested_by_role]}) · {fmtDate(a.created_at)}</span>
                    <span className="inline-flex items-center gap-0.5 text-[#3B82F6]"><ArrowUpCircle className="w-3 h-3" /> needs {ROLE_LABEL[a.required_role] || 'Super Admin'}+</span>
                    {a.kind === 'discount' && a.tier_name && <span>· Tier {a.tier_name} (cap {a.suggested_cap_pct}%)</span>}
                    {a.kind === 'discount' && a.discount_pct > (a.suggested_cap_pct || 0) && <span className="text-red-500 font-medium">above cap</span>}
                    {a.invoice_number && <span>· {a.invoice_number}</span>}
                  </div>
                  {a.decision_note && <div className="text-[11px] text-slate-400 italic mt-0.5">Note: {a.decision_note} — {a.decided_by_name}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill value={a.status} map={APPROVAL_STATUS} />
                  {a.can_decide && (
                    <>
                      <Button size="sm" data-testid={`approve-${a.id}`} onClick={() => setDecision({ approval: a, action: 'approve' })}
                        className="h-8 rounded-lg text-white text-xs border-0" style={{ background: GRAD }}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button size="sm" data-testid={`reject-${a.id}`} variant="outline" onClick={() => setDecision({ approval: a, action: 'reject' })}
                        className="h-8 rounded-lg text-red-600 border-red-200 text-xs">
                        <X className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            <Pagination data={data} onPage={setPage} />
          </>
        )}
      </div>

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>
              {decision?.action === 'approve' ? 'Approve' : 'Reject'} {decision?.approval.kind} for {decision?.approval.institution_name}?
            </DialogTitle>
            <DialogDescription>
              {decision?.action === 'approve'
                ? decision?.approval.kind === 'discount'
                  ? 'The discount will apply to all future bills for this institution.'
                  : 'The invoice will be marked refunded immediately.'
                : 'The requester will see your rejection note.'}
            </DialogDescription>
          </DialogHeader>
          <Textarea data-testid="decision-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note…" />
          <Button data-testid="decision-confirm-btn" disabled={busy} onClick={decide}
            className={`w-full rounded-xl text-white border-0 ${decision?.action === 'reject' ? 'bg-red-600 hover:bg-red-700' : ''}`}
            style={decision?.action === 'approve' ? { background: GRAD } : undefined}>
            {busy ? 'Saving…' : decision?.action === 'approve' ? 'Approve request' : 'Reject request'}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
